import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Line } from '@ant-design/plots';
import dayjs from 'dayjs';
import { Table, Card, Col, Tag, Typography, Spin } from 'antd';
import { APIClient } from '../../helpers/api_helper';
import Breadcrumb from '../../Common/Breadcrumb';
import MoneyDisplay from '../../Common/MoneyDisplay';
import IntentsTable from '../../Common/IntentsTable';
import utils from '../../Common/utils';
import { setInitialFilterConf } from '../../slices/filters/thunk'
import { useSelector, useDispatch } from "react-redux";
import IntentDrawer from '../../Common/IntentDrawer'
import WarnTriangle from "../../Common/WarnTriangle";
import { useTheme }  from '../../Common/ThemeContext';


const { Text } = Typography;
const api = new APIClient();


const showRowWarning = (record) => {
  return record.amount_diff != 0 && record.amount_diff != null
}

const dayCount = (n) => (n > 0 ? ` (${n})` : null);

const CashflowWithLines = () => {
  document.title = 'Denarni tok' + process.env.REACT_APP_PAGE_TITLE;

  const dispatch = useDispatch();
  const filterdata = useSelector((state) => state.Filterdata.data)
  const { theme } = useTheme();
  const [cashflowData, setCashflowData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [editedIntentId, setEditedIntentId] = useState(null);
  const [intentDrawerOpen, setIntentDrawerOpen] = useState(false);
  // { [isoDate]: { loading, results } } for the expanded rows. The ref mirrors
  // it so loadDay's "already fetched?" guard can be read synchronously —
  // checking inside the state updater would still let the request through.
  const [dayIntents, setDayIntents] = useState({});
  const dayIntentsRef = useRef({});


  // Send only the filters this page actually shows (see setInitialFilterConf
  // below). Filter state is shared app-wide and rides along in the URL hash,
  // so anything else that happens to be set — e.g. `unpaid_only` from a
  // dashboard tile — would silently reshape the projection with no control
  // on screen to clear it.
  const { date_range, account } = filterdata;

  const loadCashFlowData = useCallback(() => {
    setLoading(true);
    // The day rows carry totals and counts only; a row's intents are fetched
    // from /api/cashflow/day/ when it is actually expanded. Reloading the
    // summary invalidates that cache, since amounts may have just changed.
    dayIntentsRef.current = {};
    setDayIntents({});
    api.get('/api/cashflow/', { date_range, account }).then((resp) => {
      setCashflowData(resp);
      setLoading(false);
    });
  }, [date_range, account]);

  // Fetch the intents behind an expanded day, once.
  const loadDay = useCallback((date) => {
    if (dayIntentsRef.current[date]) { return; }
    const pending = { loading: true, results: [] };
    dayIntentsRef.current = { ...dayIntentsRef.current, [date]: pending };
    setDayIntents(prev => ({ ...prev, [date]: pending }));

    const settle = (results) => {
      const loaded = { loading: false, results };
      dayIntentsRef.current = { ...dayIntentsRef.current, [date]: loaded };
      setDayIntents(prev => ({ ...prev, [date]: loaded }));
    };
    api.get('/api/cashflow/day/', { date, account })
      .then(resp => settle(resp || []))
      .catch(() => settle([]));
  }, [account]);

  useEffect(() => {
    dispatch(setInitialFilterConf({
      data: {'date_range': utils.getCurrentYear()},
      // Only date range + account.
      hidden: ['intent_type', 'intent_checkboxes_group', 'search',
               'tags', 'companies', 'persons', 'category', 'projects'],
      disabled: []
    }))
  }, []);


  // Depend on the two filters this page sends rather than on the whole
  // `filterdata` object: every filter write produces a new object identity
  // (updateFilterData also injects `page`), which would refetch for nothing.
  useEffect(() => {
    if (date_range){
      loadCashFlowData()
    }
  }, [date_range, account, loadCashFlowData]);

  // Stable identities: IntentDrawer is mounted even while closed and is not
  // memoized, so a fresh arrow per render re-rendered its whole form tree on
  // every Cashflow render.
  const onDrawerCancel = useCallback(() => {
    setIntentDrawerOpen(false);
    setEditedIntentId(null);
  }, []);

  const onDrawerDone = useCallback(() => {
    loadCashFlowData();
    setEditedIntentId(null);
    setIntentDrawerOpen(false);
  }, [loadCashFlowData]);

  const onDrawerDelete = useCallback(() => {
    console.error('DEL Not implemented');
  }, []);

  // Refresh whichever days are open after a reload replaced their cache.
  const expandedRef = useRef(expandedRowKeys);
  expandedRef.current = expandedRowKeys;
  useEffect(() => {
    if (loading) { return; }
    cashflowData
      .filter(row => expandedRef.current.includes(row.dt_str))
      .forEach(row => loadDay(row.date));
  }, [cashflowData, loading, loadDay]);

    // Prepare data for line chart. Memoized: @ant-design/plots diffs `data` by
    // reference, so rebuilding this array on every render made the G2 canvas
    // re-render and re-animate even when nothing had changed.
    const lineData = useMemo(() => [
      ...cashflowData.map(row => ({
        dt_str: row.dt_str,
        value: row.balance_realised,
        type: 'Realizirano',
      })),
      ...cashflowData.map(row => ({
        dt_str: row.dt_str,
        value: row.balance_planned,
        type: 'Načrtovano',
      })),
      // ...cashflowData.map(row => ({
      //   dt_str: row.dt_str,
      //   value: row.balance_diff,
      //   type: 'Balance Δ (planned vs realized)',
      // })),
    ], [cashflowData]);

  // Vertical marker at today. The x scale is categorical (dt_str), so the
  // annotation must point at a category that actually exists in the data --
  // G2 cannot place it otherwise. Rows carry a raw ISO `date`, so match on that
  // rather than re-formatting the localised dt_str.
  // A Set, not the raw array: rowClassName runs per row on every render, and
  // Array.includes there made the lookup O(rows x expanded).
  const expandedKeySet = useMemo(() => new Set(expandedRowKeys), [expandedRowKeys]);

  // Row the user jumped to by clicking a point on the chart.
  const [focusedDt, setFocusedDt] = useState(null);
  const tableWrapRef = useRef(null);

  const focusTableRow = useCallback((dtStr) => {
    if (!dtStr) return;
    setFocusedDt(dtStr);
    // The table is unpaginated, so every row is in the DOM and addressable by
    // antd's data-row-key (rowKey="dt_str"). Scoped to the wrapper so the nested
    // per-day IntentsTables cannot be matched instead.
    const row = tableWrapRef.current?.querySelector(`[data-row-key="${dtStr}"]`);
    row?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  const todayIso = useMemo(() => dayjs().format('YYYY-MM-DD'), []);
  const todayRow = useMemo(
    () => cashflowData.find(row => row.date === todayIso),
    [cashflowData, todayIso]
  );

  const config = useMemo(() => ({
    data: lineData,
    xField: 'dt_str',
    yField: 'value',
    seriesField: 'type',
    smooth: false,
    color: ['orange', 'green', 'black'], // Paid, Planned, Amount Δ, Balance Δ
    tooltip: {
      title: x => `${x}`,
      shared: true,
      showMarkers: true,
      formatter: (data) => ({
        name: `${data.type}`,
        value: `${utils.formatToMoney(data.value)}`,
      }),
    },
    xAxis: {
      label: {
        rotate: -45,
        autoHide: true,
        autoRotate: false,
      },
    },
    yAxis: {
      label: {
        formatter: val => utils.formatToThousands(val) + " €",
      },
    },
    legend: {
      position: 'top',
    },
    // Empty when today falls outside the selected range (e.g. viewing a past month).
    annotations: todayRow ? [{
      type: 'line',
      start: [todayRow.dt_str, 'min'],
      end: [todayRow.dt_str, 'max'],
      style: {
        stroke: '#ff4d4f',
        lineWidth: 1.5,
        lineDash: [4, 4],
      },
      text: {
        content: 'Danes',
        // 'end' is the 'max' end of the line, i.e. the top of the plot.
        position: 'end',
        // G2 rotates annotation text to follow the line by default, which on a
        // vertical line reads sideways. Keep it horizontal.
        autoRotate: false,
        offsetY: -8,
        style: {
          fill: '#ff4d4f',
          fontSize: 11,
          textAlign: 'center',
          fontWeight: 500,
        },
      },
    }] : [],
    meta: {
      dt_str: { alias: 'Datum' },
      value: {
        alias: 'Stanje',
        formatter: (v) => `${v.toFixed(2)}`,
      },
    },
    onReady: (plot) => {
      // 'plot:click' rather than 'element:click': the points on a line are small
      // targets, and getTooltipItems() snaps to the nearest x category, so a
      // click anywhere in that column resolves to the right day.
      plot.on('plot:click', (evt) => {
        try {
          const items = plot.chart.getTooltipItems({ x: evt.x, y: evt.y });
          focusTableRow(items?.[0]?.data?.dt_str);
        } catch (e) {
          // Clicking outside the plot area resolves to nothing; not an error.
        }
      });
    },
  }), [lineData, todayRow, focusTableRow]);

  const tableCols = useMemo(() => [
    {
      title: 'Datum',
      dataIndex: 'dt_str',
      key: 'dt_str',
      align: 'center',
      // Headroom so the absolutely-positioned "Danes" label below has somewhere
      // to sit without reaching the date.
      width: 160,
      className: 'group-date-header with-right-border',
      render: (val, record) => (
        record.date === todayIso ? (
          // The label is taken OUT OF FLOW and hung to the left of the date, so
          // the date itself stays centred exactly where it is on every other
          // row. Putting the label in flow (either side) would shift it.
          <span style={{ position: 'relative', display: 'inline-block' }}>
            <Tag
              color="red"
              bordered={false}
              style={{
                position: 'absolute',
                right: '100%',
                top: '50%',
                transform: 'translateY(-50%)',
                marginRight: 6,
                marginInlineEnd: 6,
                padding: '0 5px',
                // Compact: the table is size="small", so a default-sized Tag
                // would push the row taller.
                fontSize: 10,
                lineHeight: '16px',
              }}
            >
              Danes
            </Tag>
            <b style={{ color: '#ff4d4f' }}>{val}</b>
          </span>
        ) : <b>{val}</b>
      ),
    },
    {
      title: 'Status',
      className: 'group-diff-header with-right-border',
      children: [
        // {
        //   title: 'Day Δ (Planned - Realized)',
        //   dataIndex: 'amount_diff',
        //   key: 'amount_diff',
        //   render: val => <MoneyDisplay type={val < 0 ? 'EXPENSE' : 'INCOME'} val={val} />,
        // },
        // {
        //   title: 'Balance Δ (Planned - Realized)',
        //   dataIndex: 'balance_diff',
        //   key: 'balance_diff',
        //   className: 'with-right-border ',
        //   render: val => <MoneyDisplay type={val < 0 ? 'EXPENSE' : 'INCOME'} val={val} />,
        // },
        {
          title: '',
          dataIndex: 'amount_diff',
          key: 'amount_diff',
          className: 'with-right-border status-col',
          align: 'center',
          render: (val, record) => {
            if (showRowWarning(record)) {
              const unpaidIntentsCount = record.n_unpaid;
              return (
                <div className="status-cell-content">
                  <WarnTriangle tooltip={`Neplačane transakcije: ${unpaidIntentsCount}`} />
                  <Text style={{ marginLeft: 10 }}>
                    {unpaidIntentsCount}
                  </Text>
                </div>
              );
            }
            return null;
          },
        }
      ],
    },
    {
      title: 'NAČRTOVANO',
      className: 'group-planned-header with-right-border',
      children: [
        {
          title: 'Dnevni znesek',
          dataIndex: 'amount_planned',
          key: 'amount_planned',
          render: (val, record) => (
            <>
              <MoneyDisplay type={val < 0 ? 'EXPENSE' : 'INCOME'} val={val} />
              {dayCount(record.n_planned)}
            </>
          ),
        },
        {
          title: 'Stanje',
          dataIndex: 'balance_planned',
          key: 'balance_planned',
          className: 'with-right-border ',
          render: val => <MoneyDisplay type={val < 0 ? 'EXPENSE' : 'INCOME'} val={val} />,
        },
      ],
    },
    {
      title: 'REALIZIRANO',
      className: 'group-realized-header with-right-border',
      children: [
        {
          title: 'Dnevni znesek',
          dataIndex: 'amount_realised',
          key: 'amount_realised',
          render: (val, record) => (
            <>
              <MoneyDisplay type={val < 0 ? 'EXPENSE' : 'INCOME'} val={val} />
              {dayCount(record.n_realised)}
            </>
          ),
        },
        {
          title: 'Stanje',
          dataIndex: 'balance_realised',
          key: 'balance_realised',
          className: 'with-right-border ',
          render: val => <MoneyDisplay type={val < 0 ? 'EXPENSE' : 'INCOME'} val={val} />,
        },
      ],
    },
  ], [todayIso]);

  return (
    <>
      <Breadcrumb parentTitle="Vpogledi" pageTitle="Denarni tok" />

      <IntentDrawer
        open={intentDrawerOpen}
        onCancel={onDrawerCancel}
        onDone={onDrawerDone}
        onIntentChanged={loadCashFlowData}
        onDelete={onDrawerDelete}
        editedIntentId={editedIntentId}
      />

      <Col xs={24} style={{ marginTop: 16 }}>
        <Card>
          <Line {...config} loading={loading} />
        </Card>
      </Col>

      <Col xs={24} style={{ marginTop: 24 }}>
        <Card>
          <div ref={tableWrapRef}>
          <Table
            dataSource={cashflowData}
            columns={tableCols}
            pagination={false}
            rowKey="dt_str"
            size="small"
            loading={loading}
            expandedRowKeys={expandedRowKeys}
            onExpandedRowsChange={setExpandedRowKeys}
            rowClassName={(record) => {
              // Composed, not early-returned: today's marker must survive a row
              // that is also expanded or carrying a warning.
              const classes = [];
              if (record.dt_str === focusedDt) {
                classes.push('row-cashflow-focus')
              }
              if (record.date === todayIso) {
                classes.push('row-today')
              }
              if (expandedKeySet.has(record.dt_str)){
                classes.push('row-selected')
              } else if (showRowWarning(record)){
                classes.push(theme === 'dark' ? 'row-warning-dark' : 'row-warning')
              }
              return classes.join(' ')
            }}
            expandable={{
              onExpand: (expanded, record) => {
                if (expanded) { loadDay(record.date); }
              },
              expandedRowRender: (record) => {
                const day = dayIntents[record.date];
                // The row's intents are fetched when it is opened, and again
                // whenever a reload invalidates the cache -- so hold the panel
                // on a spinner until they land instead of flashing an empty one.
                if (!day || day.loading) {
                  return (
                    <div style={{ padding: 24, textAlign: 'center' }}>
                      <Spin />
                    </div>
                  );
                }
                return (
                  <>
                    {day.results.length > 0 && (
                      <IntentsTable
                        intentsData={{ results: day.results }}
                        hidePagination
                        hideColumnConfig
                        onIntentEdit={r => {
                          setEditedIntentId(r.id)
                          setIntentDrawerOpen(true);
                        }}
                        onIntentChanged={() => loadCashFlowData()}
                      />
                    )}
                  </>
                );
              },
              rowExpandable: (record) =>
                record.n_planned > 0 || record.n_realised > 0,
            }}
          />
          </div>
        </Card>
      </Col>
    </>
  );
};

export default CashflowWithLines;
